import React, { useEffect, useRef } from 'react';
import { PieChart } from 'lucide-react';

interface RiskProspect {
  id: string;
  lead_source: string;
  // Other properties not needed for this component
  [key: string]: any;
}

interface LeadSourceChartProps {
  prospects: RiskProspect[];
}

const LeadSourceChart: React.FC<LeadSourceChartProps> = ({ prospects }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !prospects.length) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    // Count leads by source
    const sourceCount: Record<string, number> = {};
    prospects.forEach(prospect => {
      sourceCount[prospect.lead_source] = (sourceCount[prospect.lead_source] || 0) + 1;
    });

    // Convert to array for easier processing
    const sources = Object.entries(sourceCount).map(([source, count]) => ({ source, count }));
    
    // Sort by count (descending)
    sources.sort((a, b) => b.count - a.count);

    // Define colors
    const colors = [
      '#3B82F6', // Blue
      '#10B981', // Green
      '#F97316', // Orange
      '#8B5CF6', // Purple
      '#EC4899', // Pink
      '#6366F1', // Indigo
      '#14B8A6', // Teal
      '#F59E0B', // Amber
    ];

    // Canvas dimensions
    const width = canvasRef.current.width;
    const height = canvasRef.current.height;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(centerX, centerY) * 0.8;
    
    // Draw pie chart
    let startAngle = 0;
    const total = sources.reduce((sum, { count }) => sum + count, 0);
    
    sources.forEach(({ source, count }, i) => {
      const sliceAngle = (count / total) * 2 * Math.PI;
      const endAngle = startAngle + sliceAngle;
      
      // Draw slice
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, startAngle, endAngle);
      ctx.closePath();
      
      ctx.fillStyle = colors[i % colors.length];
      ctx.fill();
      
      // Calculate label position
      const midAngle = startAngle + sliceAngle / 2;
      const labelRadius = radius * 0.7;
      const labelX = centerX + Math.cos(midAngle) * labelRadius;
      const labelY = centerY + Math.sin(midAngle) * labelRadius;
      
      // Draw percentage label if slice is big enough
      if (sliceAngle > 0.2) {
        const percentage = Math.round((count / total) * 100);
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${percentage}%`, labelX, labelY);
      }
      
      startAngle = endAngle;
    });
    
    // Draw legend
    const legendX = 10;
    let legendY = height - 10 - (sources.length * 20);
    
    sources.forEach(({ source, count }, i) => {
      // Draw color box
      ctx.fillStyle = colors[i % colors.length];
      ctx.fillRect(legendX, legendY - 8, 12, 12);
      
      // Draw label
      ctx.fillStyle = '#374151'; // Gray-700
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      const percentage = Math.round((count / total) * 100);
      ctx.fillText(`${source} (${percentage}%)`, legendX + 18, legendY - 2);
      
      legendY += 20;
    });
    
  }, [prospects]);

  if (!prospects.length) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-400">
        <PieChart className="h-12 w-12 mb-2" />
        <p>No data available</p>
      </div>
    );
  }

  return (
    <canvas 
      ref={canvasRef} 
      width={500} 
      height={250}
      className="w-full h-full"
    />
  );
};

export default LeadSourceChart;